import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Hash, CheckCircle, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { stcTransService } from '../../../../../services/stcTransService';

interface SerialTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (selectedSerials: string[]) => void;
  itemNo: string;
  itemDescription: string;
  binCode: string;
  warehouseId: string;
}

interface SerialItem {
  serialNo: string;
  binCode: string;
  warehouseId: string;
}

export const SerialTransferModal: React.FC<SerialTransferModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  itemNo,
  itemDescription,
  binCode,
  warehouseId
}) => {
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevIsOpenRef = useRef(false);

  const [availableSerials, setAvailableSerials] = useState<SerialItem[]>([]);
  const [filteredSerials, setFilteredSerials] = useState<SerialItem[]>([]);
  const [selectedSerials, setSelectedSerials] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setError(null);
      setConfirming(false);
      setSearchTerm('');
      setSelectedSerials(new Set());
      setAvailableSerials([]);
      setFilteredSerials([]);
      loadAvailableSerials();
      setTimeout(() => inputRef.current?.focus(), 100);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter serials based on search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredSerials(availableSerials);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredSerials(
        availableSerials.filter(s => s.serialNo.toLowerCase().includes(term))
      );
    }
  }, [searchTerm, availableSerials]);

  const loadAvailableSerials = async () => {
    setLoading(true);
    setError(null);
    try {
      const serials = await stcTransService.getSerialNumbersAtBin(itemNo, warehouseId, binCode);
      setAvailableSerials(serials);
      setFilteredSerials(serials);
      if (serials.length === 0) {
        setError('Ingen serienumre tilgjengelig på denne lokasjonen');
      }
    } catch (err: any) {
      console.error('Error loading serial numbers:', err);
      setError(err.message || 'Kunne ikke hente serienumre');
    } finally {
      setLoading(false);
    }
  };

  const toggleSerial = (serialNo: string) => {
    setSelectedSerials(prev => {
      const next = new Set(prev);
      if (next.has(serialNo)) {
        next.delete(serialNo);
      } else {
        next.add(serialNo);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedSerials.size === filteredSerials.length) {
      // Deselect all
      setSelectedSerials(new Set());
    } else {
      // Select all filtered
      setSelectedSerials(new Set(filteredSerials.map(s => s.serialNo)));
    }
  };

  const handleScanInput = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      e.preventDefault();
      const exactMatch = availableSerials.find(
        s => s.serialNo.toLowerCase() === searchTerm.trim().toLowerCase()
      );
      if (exactMatch) {
        toggleSerial(exactMatch.serialNo);
        setSearchTerm('');
      } else {
        setError('Serienummeret finnes ikke på denne lokasjonen');
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (selectedSerials.size === 0) return;
    setConfirming(true);
    onComplete(Array.from(selectedSerials));
  };

  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  const allSelected = filteredSerials.length > 0 && selectedSerials.size === filteredSerials.length;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-0 sm:p-4"
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-lg sm:max-h-[90vh] flex flex-col overflow-hidden"
        onClick={stopPropagation}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-purple-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Hash className="text-purple-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Velg serienumre for overf&oslash;ring
              </h2>
              <p className="text-sm text-gray-500">{itemNo} - {itemDescription}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Item/Location info */}
        <div className="px-4 pt-3 pb-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Fra: <span className="font-medium text-gray-900">{binCode}</span></span>
            <ArrowRight className="h-3 w-3 text-gray-400" />
            {availableSerials.length > 0 && (
              <span className="ml-auto">Tilgjengelig: <span className="font-medium text-gray-900">{availableSerials.length}</span></span>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setError(null); }}
              onKeyDown={handleScanInput}
              placeholder="Skann eller s&oslash;k serienummer..."
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              autoComplete="off"
            />
          </div>

          {error && (
            <div className="mt-2 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Select all toggle */}
        {!loading && filteredSerials.length > 0 && (
          <div className="px-4 pb-2">
            <button
              onClick={toggleAll}
              className="text-sm text-purple-600 hover:text-purple-800 font-medium"
            >
              {allSelected ? 'Fjern alle markeringer' : `Velg alle (${filteredSerials.length})`}
            </button>
          </div>
        )}

        {/* Serial list */}
        <div className="flex-1 overflow-y-auto px-4 pb-2">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-gray-500 py-8">
              <Loader2 className="animate-spin" size={20} />
              Henter serienumre...
            </div>
          ) : filteredSerials.length === 0 && !error ? (
            <div className="text-center text-gray-500 py-8">
              {searchTerm ? 'Ingen treff' : 'Ingen serienumre tilgjengelig'}
            </div>
          ) : (
            <div className="space-y-1.5">
              {filteredSerials.map((serial) => {
                const isSelected = selectedSerials.has(serial.serialNo);
                return (
                  <button
                    key={serial.serialNo}
                    onClick={() => toggleSerial(serial.serialNo)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left ${
                      isSelected
                        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isSelected ? 'bg-purple-500 border-purple-500' : 'border-gray-300'
                      }`}>
                        {isSelected && <CheckCircle className="text-white" size={14} />}
                      </div>
                      <span className="font-mono font-medium text-gray-900">{serial.serialNo}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Avbryt
          </button>
          <button
            onClick={handleConfirm}
            disabled={selectedSerials.size === 0 || confirming}
            className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
              selectedSerials.size > 0
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {confirming ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <CheckCircle size={16} />
            )}
            {`Overfør ${selectedSerials.size} serienumre`}
          </button>
        </div>
      </div>
    </div>
  );
};
